import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, UploadCloud, Download, Loader2, CheckCircle2 } from "lucide-react";
import api, { API } from "@/lib/api";

const STANDARD_FIELDS = [
  ["", "Ignore this column"], ["first_name", "First Name"], ["last_name", "Last Name"], ["email", "Email"],
  ["phone", "Mobile Number"], ["whatsapp_number", "WhatsApp Number"], ["country", "Country"], ["state", "State"],
  ["city", "City"], ["company", "Company"], ["job_title", "Job Title"], ["experience", "Experience"],
  ["interested_course", "Interested Course"], ["tags", "Tags"], ["notes", "Notes"],
];

export default function ImportWizardModal({ onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [importDoc, setImportDoc] = useState(null);
  const [mapping, setMapping] = useState({});
  const [duplicateStrategy, setDuplicateStrategy] = useState("update");
  const [defaultTags, setDefaultTags] = useState("");
  const [starting, setStarting] = useState(false);
  const [status, setStatus] = useState(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/crm/import/upload", form);
      setImportDoc(data);
      setMapping(data.mapping);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read this file");
    } finally {
      setUploading(false);
    }
  };

  const startImport = async () => {
    setStarting(true);
    try {
      await api.post(`/admin/crm/import/${importDoc.id}/start`, {
        mapping, duplicate_strategy: duplicateStrategy,
        default_tags: defaultTags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      setStep(3);
      pollRef.current = setInterval(async () => {
        const { data } = await api.get(`/admin/crm/import/${importDoc.id}`);
        setStatus(data);
        if (data.status === "completed" || data.status === "failed") {
          clearInterval(pollRef.current);
          onImported?.();
        }
      }, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start import");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="crm-import-wizard-modal">
      <div className="glass-panel rounded-2xl p-5 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-semibold text-white">Import Contacts — Step {step} of 3</p>
          <button onClick={onClose} data-testid="crm-close-import-wizard-btn"><X className="w-4 h-4 text-slate-400 hover:text-white" /></button>
        </div>

        {step === 1 && (
          <div data-testid="crm-import-step-upload">
            <div onClick={() => fileInputRef.current?.click()}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }} onDragOver={(e) => e.preventDefault()}
              data-testid="crm-import-dropzone"
              className="border-2 border-dashed border-white/15 rounded-xl p-10 text-center cursor-pointer hover:border-primary/50 transition-colors">
              <UploadCloud className="w-8 h-8 text-slate-500 mx-auto mb-3" />
              <p className="text-sm text-slate-300">{uploading ? "Uploading…" : "Click or drag a CSV / Excel file here"}</p>
              <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" data-testid="crm-import-file-input" className="hidden"
                onChange={(e) => handleFile(e.target.files[0])} />
            </div>
            <a href={`${API}/admin/crm/import/sample-template`} data-testid="crm-download-sample-template-btn"
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mt-3 w-fit">
              <Download className="w-3.5 h-3.5" /> Download sample template
            </a>
          </div>
        )}

        {step === 2 && importDoc && (
          <div data-testid="crm-import-step-mapping">
            <p className="text-xs text-slate-400 mb-3">{importDoc.total_rows} rows detected. Confirm the column mapping below.</p>
            <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
              {importDoc.columns.map((col) => (
                <div key={col} className="flex items-center gap-3" data-testid={`crm-import-mapping-row-${col}`}>
                  <span className="text-xs text-slate-300 w-1/2 truncate">{col}</span>
                  <select value={mapping[col] || ""} onChange={(e) => setMapping({ ...mapping, [col]: e.target.value || null })}
                    data-testid={`crm-import-mapping-select-${col}`}
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white">
                    {STANDARD_FIELDS.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <p className="text-[11px] text-slate-500 mb-1.5">Duplicate Handling</p>
                <select value={duplicateStrategy} onChange={(e) => setDuplicateStrategy(e.target.value)} data-testid="crm-import-duplicate-strategy-select"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-xs text-white">
                  <option value="update">Update existing (overwrite blanks)</option>
                  <option value="merge">Merge existing (fill blanks only)</option>
                  <option value="skip">Skip existing</option>
                </select>
              </div>
              <div>
                <p className="text-[11px] text-slate-500 mb-1.5">Default Tags (comma separated)</p>
                <input value={defaultTags} onChange={(e) => setDefaultTags(e.target.value)} placeholder="e.g. csv_import_feb" data-testid="crm-import-default-tags-input"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-xs text-white" />
              </div>
            </div>
            <button onClick={startImport} disabled={starting} data-testid="crm-start-import-btn"
              className="bg-primary hover:bg-primary/90 text-white text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
              {starting ? "Starting…" : "Start Import"}
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="text-center py-6" data-testid="crm-import-step-progress">
            {!status || status.status === "processing" ? (
              <>
                <Loader2 className="w-8 h-8 text-primary mx-auto mb-3 animate-spin" />
                <p className="text-sm text-slate-300">Importing your contacts… ({status?.processed || 0}/{importDoc?.total_rows})</p>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                <p className="text-sm text-white font-semibold mb-2" data-testid="crm-import-result-summary">Import Complete</p>
                <p className="text-xs text-slate-400">
                  {status.stats.created} created · {status.stats.updated} updated · {status.stats.skipped} skipped · {status.stats.failed} failed
                </p>
                {status.stats.failed > 0 && (
                  <a href={`${API}/admin/crm/import/${importDoc.id}/errors`} data-testid="crm-download-import-errors-btn"
                    className="text-xs text-red-300 hover:text-red-200 underline mt-2 inline-block">Download error report</a>
                )}
                <button onClick={onClose} data-testid="crm-finish-import-btn"
                  className="block mx-auto mt-4 bg-white/10 hover:bg-white/15 text-white text-xs font-semibold px-4 py-2 rounded-lg">Done</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
